import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { map, Observable, of, tap } from 'rxjs';
import { environment } from '@environments/environment';
import {
  AcceptedVersions,
  AcceptedVersionStatus,
  AcceptLegalDocumentResult,
  LegalDocumentType,
} from 'src/core/interfaces/legal-acceptance.interfaces';

interface ApiEnvelope<T> {
  data: T;
}

// La verificación de re-aceptación se resuelve UNA vez por sesión y se recuerda
// acá. Vive en `sessionStorage` y no en memoria para que recargar la pestaña no
// cuente como un login nuevo: el estándar legal LATAM ata la versión al momento
// de iniciar sesión, no al momento de renderizar una vista.
const SESSION_GATE_KEY = 'pc_legal_gate';

interface ApiAcceptedVersionsDocument extends AcceptedVersionStatus {
  documentType: LegalDocumentType;
}

// REQ-ERR-01 — mapea los códigos de error del backend a mensajes accionables para el cliente.
export function mapLegalAcceptanceError(error: HttpErrorResponse): string {
  const code = error.error?.code;

  if (error.status === 422 && (code === 'terms_document_not_available' || code === 'privacy_document_not_available')) {
    return 'La institución aún no ha configurado este documento. Contacta a soporte.';
  }
  if (error.status === 401) {
    return 'Tu sesión expiró. Vuelve a iniciar sesión.';
  }
  return 'No pudimos registrar tu aceptación. Intenta nuevamente.';
}

/**
 * Los tres endpoints identifican al cliente por la cookie de sesión: no reciben compañía ni cliente.
 * El registro de consentimiento es evidencia legal, así que no puede escribirse sin sesión.
 */
@Injectable({ providedIn: 'root' })
export class LegalAcceptanceService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.workflowApiUrl}/portal/client`;

  // REQ-FUNC-07 — la solicitud en curso queda amparada por la versión aceptada
  // al iniciar sesión. Si el tenant publica una versión nueva a mitad de sesión,
  // el cliente NO se interrumpe: la desincronización se cobra en el próximo
  // login, cuando esta compuerta vuelve a estar vacía.
  //
  // Fail-closed se mantiene: un error de lectura no marca la compuerta, así que
  // el intento siguiente vuelve a preguntar.
  ensureAcceptedForSession(clientUserId: string): Observable<boolean> {
    if (this.isGateOpen(clientUserId)) return of(true);

    return this.requiresReacceptance().pipe(
      tap((required) => {
        if (!required) this.openGate(clientUserId);
      }),
      map((required) => !required)
    );
  }

  // REQ-FUNC-09 — iniciar una solicitud NUEVA sí vuelve a preguntar, aunque la
  // compuerta de la sesión esté abierta: el consentimiento es por transacción.
  requiresReacceptance(): Observable<boolean> {
    return this.getAcceptedVersions().pipe(
      map(
        (versions) =>
          !!versions.TERMS?.reacceptanceRequired || !!versions.PRIVACY_POLICY?.reacceptanceRequired
      )
    );
  }

  openGate(clientUserId: string): void {
    sessionStorage.setItem(SESSION_GATE_KEY, clientUserId);
  }

  clearGate(): void {
    sessionStorage.removeItem(SESSION_GATE_KEY);
  }

  // Se compara contra el cliente: dos sesiones distintas en la misma pestaña no
  // pueden heredar la compuerta de la anterior.
  private isGateOpen(clientUserId: string): boolean {
    return sessionStorage.getItem(SESSION_GATE_KEY) === clientUserId;
  }

  getAcceptedVersions(): Observable<AcceptedVersions> {
    return this.http
      .get<ApiEnvelope<{ documents: ApiAcceptedVersionsDocument[] }>>(`${this.baseUrl}/accepted-versions`)
      .pipe(
        map((response) =>
          (response.data?.documents ?? []).reduce<AcceptedVersions>((versions, { documentType, ...status }) => {
            versions[documentType] = status;
            return versions;
          }, {})
        )
      );
  }

  acceptTerms(): Observable<AcceptLegalDocumentResult> {
    return this.accept('accept-terms');
  }

  acceptPrivacyPolicy(): Observable<AcceptLegalDocumentResult> {
    return this.accept('accept-privacy-policy');
  }

  private accept(path: string): Observable<AcceptLegalDocumentResult> {
    return this.http
      .post<ApiEnvelope<AcceptLegalDocumentResult>>(`${this.baseUrl}/${path}`, {})
      .pipe(map((response) => ({ acceptedVersion: response.data?.acceptedVersion ?? null })));
  }
}
